import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Heading, Text, Img } from "components";
import TopMenuBar from "components/TopMenuBar";
import FooterSection from "components/FooterSection";
import { fetchBlogPosts, excerptFromMarkdown } from "services/blogApi";

const PLATFORM_LABEL = {
    substack: "Substack",
    linkedin: "LinkedIn",
};

function PostCard({ post }) {
    const excerpt = post.excerpt || excerptFromMarkdown(post.canonical_markdown);
    const date = (post.published_at || post.created_at)
        ? new Date(post.published_at || post.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
          })
        : null;

    return (
        <Link to={`/blog/${post.id}`} className="group block">
            <div className="flex flex-col h-full border border-gray-800 rounded-lg overflow-hidden bg-gray-900_02 hover:border-gray-800 transition-colors">
                {post.cover_image_url && (
                    <div className="h-48 overflow-hidden">
                        <img
                            src={post.cover_image_url}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                    </div>
                )}
                <div className="flex flex-col gap-3 p-6 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        {post.external_links.map(({ platform, url }) => (
                            <a
                                key={platform}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs px-2 py-0.5 border border-gray-800 rounded text-gray-300 hover:border-gray-300 transition-colors"
                            >
                                {PLATFORM_LABEL[platform] ?? platform}
                            </a>
                        ))}
                        {date && (
                            <span className="text-xs text-gray-800 ml-auto">{date}</span>
                        )}
                    </div>
                    <Heading as="h3" size="s" className="!text-gray-300 leading-[130%] group-hover:text-white-A700 transition-colors">
                        {post.title}
                    </Heading>
                    <Text as="p" className="text-sm leading-[150%] line-clamp-3">
                        {excerpt}
                    </Text>
                </div>
            </div>
        </Link>
    );
}

export default function BlogPage() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchBlogPosts()
            .then(setPosts)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    return (
        <>
            <Helmet>
                <title>Blog — AIHumanity</title>
            </Helmet>
            <div className="w-full min-h-screen bg-black-900_02">
                <TopMenuBar />
                <main className="max-w-[1040px] mx-auto px-5 py-16">
                    <div className="flex flex-col gap-10">
                        <Heading size="xl" as="h1" className="!text-gray-300">
                            Blog
                        </Heading>

                        {loading && (
                            <Text as="p" className="text-gray-800">
                                Loading posts…
                            </Text>
                        )}

                        {error && (
                            <Text as="p" className="text-red-400">
                                {error}
                            </Text>
                        )}

                        {!loading && !error && posts.length === 0 && (
                            <Text as="p" className="text-gray-800">
                                No posts yet.
                            </Text>
                        )}

                        <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6">
                            {posts.map((post) => (
                                <PostCard key={post.id} post={post} />
                            ))}
                        </div>
                    </div>
                </main>
                <FooterSection />
            </div>
        </>
    );
}
